import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { brandsAPI } from '@/utils/api';
import { toast } from 'sonner';
import { Zap, Store } from 'lucide-react';

const BrandRegister = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    brand_name: '',
    description: '',
    discount_min: 10,
    discount_max: 50
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await brandsAPI.register(formData);
      toast.success('Brand registered! Awaiting admin approval.');
      setTimeout(() => navigate('/brand/dashboard'), 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0f0f10]">
      {/* Navbar */}
      <nav className="border-b border-white/10 bg-black/40 backdrop-blur-md">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center space-x-2 cursor-pointer" onClick={() => navigate('/')}>
            <Zap className="h-8 w-8 text-yellow-400" />
            <span className="text-2xl font-bold bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent">
              LuxLyx
            </span>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-6 py-12">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-12">
            <Store className="h-16 w-16 text-yellow-400 mx-auto mb-4" />
            <h1 className="text-4xl font-bold mb-4">Register Your Brand</h1>
            <p className="text-xl text-gray-400">
              Join LuxLyx to liquidate your overstock inventory
            </p>
          </div>

          <div className="bg-gradient-to-br from-[#1a1a1c] to-[#0f0f10] border border-white/10 rounded-2xl p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="brand_name" className="text-gray-300">Brand Name</Label>
                <Input
                  id="brand_name"
                  data-testid="brand-name-input"
                  type="text"
                  value={formData.brand_name}
                  onChange={(e) => setFormData({ ...formData, brand_name: e.target.value })}
                  required
                  className="mt-2 bg-black/40 border-white/10 text-white"
                />
              </div>
              
              <div>
                <Label htmlFor="description" className="text-gray-300">Brand Description</Label>
                <Textarea
                  id="description"
                  data-testid="description-input"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                  rows={4}
                  className="mt-2 bg-black/40 border-white/10 text-white"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="discount_min" className="text-gray-300">Min Discount (%)</Label>
                  <Input
                    id="discount_min"
                    data-testid="discount-min-input"
                    type="number"
                    value={formData.discount_min}
                    onChange={(e) => setFormData({ ...formData, discount_min: parseFloat(e.target.value) })}
                    required
                    min="0"
                    max="100"
                    className="mt-2 bg-black/40 border-white/10 text-white"
                  />
                </div>
                <div>
                  <Label htmlFor="discount_max" className="text-gray-300">Max Discount (%)</Label>
                  <Input
                    id="discount_max"
                    data-testid="discount-max-input"
                    type="number"
                    value={formData.discount_max}
                    onChange={(e) => setFormData({ ...formData, discount_max: parseFloat(e.target.value) })}
                    required
                    min="0"
                    max="100"
                    className="mt-2 bg-black/40 border-white/10 text-white"
                  />
                </div>
              </div>

              <Button
                data-testid="submit-btn"
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-yellow-400 to-orange-500 text-black font-bold text-lg py-6 hover:opacity-90"
              >
                {loading ? 'Registering...' : 'Register Brand'}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BrandRegister;
